import ReactDOM from "react-dom/client";
import "./index.css";

/**
 * The two non-standard memory APIs this entry point probes for. `measureUserAgentSpecificMemory`
 * needs cross-origin isolation (Chrome 89+); `memory` is the older Chrome-only fallback. Neither is
 * declared on lib.dom's `Performance`, and both are feature-detected before use.
 */
type PerformanceWithMemoryApis = Performance & {
  measureUserAgentSpecificMemory?: () => Promise<{
    bytes: number;
    breakdown: Array<{
      bytes: number;
      types: string[];
      attribution?: Array<{ url?: string; scope?: string }>;
    }>;
  }>;
  memory?: { usedJSHeapSize: number; jsHeapSizeLimit: number; totalJSHeapSize?: number };
};
import { BrowserRouter } from "react-router-dom";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import * as Sentry from "@sentry/react";
import { scrubShareEvent, sentryBeforeSend } from "../sentryConstant";
import { injectScripts } from "./../injectScripts";
import { getBackendUrl } from "@Utils";
import { initPostHog } from "./services/posthog";
import { startLongFrameMonitor } from "./services/longFrameMonitor";
import { captureMaintenanceBypass } from "@Features/platform-status";

injectScripts();
initPostHog();

// index.html always ships this node; failing loudly here beats the opaque error React throws when
// handed `null`, and it is the only place the mount point is looked up.
const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error('Mount point "#root" is missing from index.html');
}
const root = ReactDOM.createRoot(rootElement);

if (process.env.REACT_APP_SENTRY_DSN) {
  const backendUrl = getBackendUrl();
  // CP-283 recipient pages: no Session Replay. Replay here runs unmasked (maskAllText:false,
  // blockAllMedia:false) with replaysOnErrorSampleRate 1.0, which on any error would upload a pixel
  // record of a STRANGER viewing shared project content — plus the token-bearing URL — to Sentry
  // SaaS. Share views always start as a full document load on /share/..., so deciding at init time
  // is complete. Errors themselves still report (with the URL scrubbed in sentryBeforeSend).
  const isShareRecipientPage = globalThis.location.pathname.startsWith("/share/");
  Sentry.init({
    release: process.env.REACT_APP_VERSION,
    environment: process.env.REACT_APP_ENV,
    dsn: process.env.REACT_APP_SENTRY_DSN,
    sendDefaultPii: true,
    integrations: [
      Sentry.browserTracingIntegration({
        enableLongAnimationFrame: true,
        enableLongTask: true,
        enableInp: true,
      }),
      Sentry.browserProfilingIntegration(),
    ],
    replaysOnErrorSampleRate: isShareRecipientPage ? 0 : 1.0,
    tracePropagationTargets: backendUrl ? [backendUrl] : [],
    replaysSessionSampleRate: isShareRecipientPage ? 0 : 0.1,
    tracesSampleRate: 0.05,
    // Profiles name their transaction outside anything the hooks below can scrub.
    profilesSampleRate: isShareRecipientPage ? 0 : 0.05,
    beforeSend(event, hint) {
      // `sentryConstant.js` is plain JavaScript, so `sentryBeforeSend` answers `any`. It has exactly
      // two outcomes: `null` to drop the event, or the very `event` object it was handed, scrubbed
      // in place by `scrubShareEvent`. Reading the verdict back as `unknown` and re-publishing that
      // same object keeps the SDK's `ErrorEvent | null` contract without an untyped value crossing
      // this boundary — and without changing which events are sent or what they carry.
      const verdict: unknown = sentryBeforeSend(event, hint);
      return verdict === null ? null : event;
    },
    // `beforeSend` sees ERROR events only. Transactions and spans are named from the path, so every
    // recipient page load and folder hop would otherwise upload `/share/<token>` as-is.
    beforeSendTransaction(event) {
      scrubShareEvent(event);
      return event;
    },
    beforeSendSpan(span) {
      scrubShareEvent(span);
      return span;
    },
  });
  if (!isShareRecipientPage) {
    // Replay (rrweb included) is the largest part of the SDK and nothing needs it before the first paint,
    // so it is added from its own chunk once init has run instead of being listed in `integrations`. The
    // replay sample rates passed to init above still govern it: the integration reads them off the client
    // when it is added. An error thrown before the chunk arrives has no replay attached.
    void import("./services/sentryReplay").then(({ replayIntegration }) => {
      Sentry.addIntegration(
        replayIntegration({
          networkDetailAllowUrls: backendUrl ? [backendUrl] : [],
          maskAllText: false,
          blockAllMedia: false,
        }),
      );
    });
  }
  startLongFrameMonitor();

  // Track memory spikes — requires CloudFront headers:
  // Cross-Origin-Opener-Policy: same-origin
  // Cross-Origin-Embedder-Policy: credentialless
  const MB = 1024 * 1024;
  let lastMemoryBytes = 0;

  if (window.crossOriginIsolated && (performance as PerformanceWithMemoryApis).measureUserAgentSpecificMemory) {
    // Whole-page memory (Chrome 89+, requires cross-origin isolation)
    const measureMemory = async () => {
      try {
        const result = await (performance as PerformanceWithMemoryApis).measureUserAgentSpecificMemory!();
        const totalMB = result.bytes / MB;
        const deltaMB = (result.bytes - lastMemoryBytes) / MB;

        // Breadcrumb only: no Sentry event is raised for a spike until alert thresholds are agreed.
        Sentry.addBreadcrumb({
          category: "memory",
          message: `Heap: ${Math.round(totalMB)}MB (${deltaMB > 0 ? "+" : ""}${Math.round(deltaMB)}MB)`,
          level: "info",
          data: { totalMB: Math.round(totalMB), deltaMB: Math.round(deltaMB) },
        });

        lastMemoryBytes = result.bytes;
      } catch {
        // measurement failed
      }
      // Poll with 30s + random jitter (0-5s)
      setTimeout(measureMemory, 30000 + Math.random() * 5000);
    };
    setTimeout(measureMemory, 5000); // start after 5s
  } else if ((performance as PerformanceWithMemoryApis).memory) {
    // Fallback: Chrome performance.memory (JS heap only)
    const checkMemory = () => {
      const mem = (performance as PerformanceWithMemoryApis).memory!;
      const usedMB = mem.usedJSHeapSize / MB;
      const limitMB = mem.jsHeapSizeLimit / MB;
      const pct = Math.round((mem.usedJSHeapSize / mem.jsHeapSizeLimit) * 100);

      // Breadcrumb only: no Sentry event is raised for a spike until alert thresholds are agreed.
      Sentry.addBreadcrumb({
        category: "memory",
        message: `Heap: ${Math.round(usedMB)}MB / ${Math.round(limitMB)}MB (${pct}%)`,
        level: "info",
        data: { usedMB: Math.round(usedMB), limitMB: Math.round(limitMB), pct },
      });

      setTimeout(checkMemory, 30000 + Math.random() * 5000);
    };
    setTimeout(checkMemory, 5000);
  }
}

// Before the router reads the location: a staff bypass link must not stay in the address bar.
captureMaintenanceBypass();

root.render(
  // React.StrictMode is deliberately disabled.
  <BrowserRouter useTransitions={false}>
    <App />
  </BrowserRouter>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
